import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useState } from 'react';

import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';
import Chevron from '~/shared/assets/icons/chevron.svg';
import { toaster } from '~/shared/utils/toaster';
import { useAuthContext } from '~/shared/context/useAuthContext';

import { type AutoscaleRulesBuilderAPI } from './useAutoscaleRulesBuilder';
import CreateAutoscaleRulesTables from './CreateAutoscaleRulesTable/CreateAutoscaleRulesTables';
import AutoscaleRulesBuilder from './AutoscaleRulesBuilder';
import { useCreateAutoscalingRule } from './CreateAutoscaleRulesTable/hooks/useCreateAutoscalingRule';

export default function CreateAutoscaleRulesModal({
  isOpen,
  onClose: close,
  builder,
  editMode,
  ruleId,
}: {
  isOpen: boolean;
  onClose: () => void;
  builder: AutoscaleRulesBuilderAPI;
  editMode?: boolean;
  ruleId?: string;
}) {
  const [showTable, setShowTable] = useState(false);
  const createRuleMutation = useCreateAutoscalingRule(editMode ? ruleId : undefined);

  const { activeTenantName } = useAuthContext();

  // Check if at least one condition is complete (has type and at least one condition operator and at least one value)
  const hasValidCondition = builder.ruleConditions.some(
    (condition) =>
      condition.type &&
      condition.conditionOperators.some(
        (conditionOperator) => conditionOperator.condition && conditionOperator.value.length > 0
      )
  );

  const handleNext = () => {
    if (!showTable && hasValidCondition) {
      setShowTable(true);
    }
  };

  const handleBack = () => {
    setShowTable(false);
  };

  const handleClose = () => {
    setShowTable(false);
    close();
  };

  const handleCreateAndApply = async () => {
    try {
      const response = await createRuleMutation.mutateAsync({
        conditions: builder.ruleConditions,
        active: true,
      });
      const ruleIdToShow = editMode ? ruleId : response.ruleId;
      toaster({
        title: 'Success',
        message: editMode
          ? `Rule ${ruleIdToShow} has been successfully updated and applied.`
          : `Rule ${ruleIdToShow} has been successfully created and applied.`,
        variant: 'success',
        toastId: ruleIdToShow,
      });
      builder.resetBuilder();
      setShowTable(false);
      close();
    } catch (error) {
      console.error(editMode ? 'Error updating rule:' : 'Error creating and applying rule:', error);
      const ruleIdToShow = editMode ? ruleId : undefined;
      toaster({
        title: 'Error',
        message: editMode
          ? `Rule ${ruleIdToShow} failed to update. Please try again.`
          : 'Failed to create autoscaling rule. Please try again.',
        variant: 'error',
        toastId: ruleIdToShow,
      });
    }
  };

  const handleCreate = async () => {
    try {
      const response = await createRuleMutation.mutateAsync({
        conditions: builder.ruleConditions,
        active: false,
      });
      const ruleIdToShow = editMode ? ruleId : response.ruleId;
      toaster({
        title: 'Success',
        message: editMode
          ? `Rule ${ruleIdToShow} has been successfully updated.`
          : `Rule ${ruleIdToShow} has been successfully created.`,
        variant: 'success',
        toastId: ruleIdToShow,
      });
      builder.resetBuilder();
      setShowTable(false);
      close();
    } catch (error) {
      console.error(editMode ? 'Error updating rule:' : 'Error creating rule:', error);
      const ruleIdToShow = editMode ? ruleId : undefined;
      toaster({
        title: 'Error',
        message: editMode
          ? `Rule ${ruleIdToShow} failed to update. Please try again.`
          : 'Failed to create autoscaling rule. Please try again.',
        variant: 'error',
        toastId: ruleIdToShow,
      });
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        as="div"
        className="relative z-[99] focus:outline-none"
        onClose={handleClose}
      >
        <div className="fixed inset-0 p-4 w-screen overflow-y-auto bg-[#00000059] backdrop-blur-[6px]">
          <div className="flex  items-center justify-center p-20 2xl:p-40 max-h-[886px] ">
            <DialogPanel
              transition
              className="flex  max-h-[calc(100vh-80px) w-full max-w-md flex-col rounded-xl bg-white p-6 backdrop-blur-2xl duration-300 ease-out overflow-hidden data-closed:transform-[scale(95%)] data-closed:opacity-0"
            >
              <div className=" py-2.5">
                <DialogHeader
                  title={editMode ? 'Edit Autoscaling Rule' : 'Create Autoscaling Rule'}
                  subtitles={
                    editMode && ruleId ? [activeTenantName, `${ruleId}`] : [activeTenantName]
                  }
                />
              </div>

              {!showTable ? (
                <div className="pt-6 min-h-[645px] 2xl:min-h-[653px] ">
                  <AutoscaleRulesBuilder builder={builder} />
                </div>
              ) : (
                <div className="flex-1 flex flex-col pt-[5px] min-h0">
                  <CreateAutoscaleRulesTables conditions={builder.ruleConditions} />
                </div>
              )}

              <DialogFooter
                onCancel={handleClose}
                onNext={handleNext}
                onBack={showTable ? handleBack : undefined}
                nextDisabled={!hasValidCondition && !showTable}
                showTable={showTable}
                onCreateAndApply={handleCreateAndApply}
                onCreateAndNotApply={handleCreate}
                isCreating={createRuleMutation.isPending}
                editMode={editMode}
              />
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}

function DialogHeader({ title, subtitles }: { title: string; subtitles: (string | undefined)[] }) {
  return (
    <div className="flex flex-col font-family-outfit">
      <DialogTitle as="h3" className="text-2xl 2xl:text-[28px] font-[500] ">
        {title}
      </DialogTitle>
      <div className="flex divide-x-2 divide-gray-400">
        {subtitles.map((subtitle, index) => (
          <Typography
            key={index}
            variant="p3"
            fontWeight={400}
            color="text-gray-700"
            className={index > 0 ? 'pl-2' : 'pr-2'}
          >
            {subtitle}
          </Typography>
        ))}
      </div>
    </div>
  );
}

function DialogFooter({
  onCancel,
  onNext,
  onBack,
  onCreateAndApply,
  onCreateAndNotApply,
  nextDisabled,
  showTable,
  isCreating,
  editMode,
}: {
  onCancel: () => void;
  onNext: () => void;
  onBack?: () => void;
  onCreateAndApply?: () => void;
  onCreateAndNotApply?: () => void;
  nextDisabled?: boolean;
  showTable?: boolean;
  isCreating?: boolean;
  editMode?: boolean;
}) {
  return (
    <div className="mt-auto flex justify-between gap-2 pt-6 px-6 -ml-6 -mr-6  border-t border-primary-70">
      <div>
        {onBack && (
          <Button
            leftIcon={<Chevron className="text-primary-900 size-5 rotate-90" />}
            onClick={onBack}
            variant="third"
            className="px7"
            disabled={isCreating}
          >
            Back
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <Button onClick={onCancel} variant="third" className="px-5" disabled={isCreating}>
          Cancel
        </Button>
        {!showTable && (
          <Button onClick={onNext} disabled={nextDisabled} className="px-7">
            Next
          </Button>
        )}
        {showTable && onCreateAndNotApply && (
          <Button
            onClick={onCreateAndNotApply}
            variant="third"
            disabled={nextDisabled || isCreating}
            className="px-7.5"
          >
            {isCreating ? (editMode ? 'Updating...' : 'Creating...') : editMode ? 'Edit' : 'Create'}
          </Button>
        )}
        {showTable && onCreateAndApply && (
          <Button onClick={onCreateAndApply} disabled={nextDisabled || isCreating} className="px-7">
            {isCreating
              ? editMode
                ? 'Updating...'
                : 'Creating...'
              : editMode
                ? 'Edit and Apply'
                : 'Create and Apply'}
          </Button>
        )}
      </div>
    </div>
  );
}
